import { addDaysToDay, formatDate, formatILS, israelDay } from '@takumi/shared';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Columns3, Megaphone } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { PageHeader } from '@/components/AppShell';
import { Select } from '@/components/ui/form';
import { Card, CardHeader, EmptyState, PageLoader } from '@/components/ui/primitives';
import { api } from '@/lib/api';

interface Bucket {
  key: string;
  label: string;
  leads: number;
  qualified: number;
  customers: number;
  revenue: number;
  spend: number | null;
  costPerLead: number | null;
}
interface Overview {
  range: { from: string; to: string };
  totals: { leads: number; qualified: number; customers: number; revenue: number; conversionRate: number };
  bySource: Bucket[];
  byCampaign: Bucket[];
  monthly: { month: string; orders: number; revenue: number; customers: number }[];
  pipeline: { id: string; name: string; color: string; count: number; value: number }[];
  adSpendConnected: boolean;
}

const RANGES = [
  { days: 30, label: '30 ימים' },
  { days: 90, label: '90 ימים' },
  { days: 180, label: 'חצי שנה' },
  { days: 365, label: 'שנה' },
];
const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '—');
const MONTHS = ['ינו׳', 'פבר׳', 'מרץ', 'אפר׳', 'מאי', 'יוני', 'יולי', 'אוג׳', 'ספט׳', 'אוק׳', 'נוב׳', 'דצמ׳'];
const monthLabel = (m: string) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;

export function ReportsPage() {
  const [days, setDays] = useState(90);
  const to = israelDay();
  const from = addDaysToDay(to, -(days - 1));
  const { data } = useQuery({ queryKey: ['reports', from, to], queryFn: () => api.get<Overview>('/reports/overview', { from, to }) });
  if (!data) return <PageLoader />;
  const t = data.totals;
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="דוחות"
        subtitle={`${formatDate(data.range.from)} – ${formatDate(data.range.to)}`}
        actions={
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-36" aria-label="טווח תאריכים">
            {RANGES.map((r) => <option key={r.days} value={r.days}>{r.label}</option>)}
          </Select>
        }
      />
      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi label="לידים חדשים" value={t.leads.toLocaleString('he-IL')} />
        <Kpi label="לידים איכותיים" value={t.qualified.toLocaleString('he-IL')} sub={pct(t.qualified, t.leads)} />
        <Kpi label="הפכו ללקוחות" value={t.customers.toLocaleString('he-IL')} sub={`${t.conversionRate}%`} />
        <Kpi label="הכנסות מהלידים" value={formatILS(t.revenue, { decimals: false })} />
        <Kpi label="עלות לליד" value={data.adSpendConnected ? '—' : 'נדרש חיבור'} sub={data.adSpendConnected ? undefined : 'Meta / Google'} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <FunnelTable title="לפי מקור" icon={<Megaphone />} rows={data.bySource} adSpend={data.adSpendConnected} />
        <RevenueChart monthly={data.monthly} />
      </div>
      <div className="mt-5">
        <FunnelTable title="לפי קמפיין (utm)" icon={<Megaphone />} rows={data.byCampaign} adSpend={data.adSpendConnected} />
      </div>
      <Card className="mt-5">
        <CardHeader title="פייפליין פתוח" icon={<Columns3 />} />
        <ul className="divide-y divide-line/70 text-sm">
          {data.pipeline.map((p) => (
            <li key={p.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className="size-2.5 rounded-full" style={{ backgroundColor: p.color }} />
              <Link to={`/leads?stageId=${p.id}`} className="flex-1 font-semibold hover:underline">{p.name}</Link>
              <span className="text-muted tabular">{p.count} לידים</span>
              <span className="w-28 text-end font-semibold tabular">{p.value ? formatILS(p.value, { decimals: false }) : '—'}</span>
            </li>
          ))}
        </ul>
      </Card>
      <p className="mt-4 text-xs text-muted">
        "ליד איכותי" = הפך ללקוח, קיבל הצעת מחיר או דוגמית, או הגיע לשלב "מעוניין" ומעלה. הכנסות = סך ההזמנות (כולל מע״מ, ללא מבוטלות) של הלידים שנכנסו בתקופה.
        עלויות פרסום מוצגות רק לאחר חיבור Meta / Google Ads / TikTok — הן לעולם לא מוערכות.
      </p>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-semibold text-muted">{label}</div>
      <div className="mt-1 font-display text-2xl leading-none font-semibold tabular">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted">{sub}</div>}
    </Card>
  );
}

function FunnelTable({ title, icon, rows, adSpend }: { title: string; icon: React.ReactNode; rows: Bucket[]; adSpend: boolean }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader title={title} icon={icon} />
      {!rows.length ? (
        <EmptyState title="אין לידים בתקופה" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead className="bg-sunken/80 text-xs font-bold text-muted">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-end [&>th:first-child]:text-start">
                <th className="ps-4">מקור</th><th>לידים</th><th>איכותיים</th><th>לקוחות</th><th>המרה</th><th>הכנסות</th>{adSpend && <th>עלות לליד</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-line/70 tabular">
              {rows.map((r) => (
                <tr key={r.key} className="[&>td]:px-3 [&>td]:py-2 [&>td]:text-end">
                  <td className="ps-4 !text-start font-semibold">{r.label}</td>
                  <td>{r.leads}</td>
                  <td>{r.qualified}</td>
                  <td>{r.customers}</td>
                  <td>{pct(r.customers, r.leads)}</td>
                  <td>{r.revenue ? formatILS(r.revenue, { decimals: false }) : '—'}</td>
                  {adSpend && <td>{r.costPerLead != null ? formatILS(r.costPerLead) : '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Single-series bar chart: no legend (the title names it), per-bar hover, table view below. */
function RevenueChart({ monthly }: { monthly: Overview['monthly'] }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...monthly.map((m) => m.revenue));
  const H = 160;
  return (
    <Card>
      <CardHeader title="הכנסות לפי חודש (12 חודשים)" icon={<BarChart3 />} />
      {!monthly.length ? (
        <EmptyState title="אין הזמנות עדיין" />
      ) : (
        <div className="p-4">
          <div className="relative flex items-end gap-0.5 border-b border-line" style={{ height: H }} role="img" aria-label="הכנסות לפי חודש">
            {monthly.map((m, i) => (
              <div key={m.month} className="relative flex h-full flex-1 items-end" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <div
                  className="w-full rounded-t bg-matcha-600 transition-opacity"
                  style={{ height: `${Math.max(2, (m.revenue / max) * (H - 8))}px`, opacity: hover === null || hover === i ? 1 : 0.55 }}
                />
                {hover === i && (
                  <div className="pointer-events-none absolute bottom-full start-1/2 z-10 mb-1 -translate-x-1/2 rounded-md bg-matcha-950 px-2 py-1 text-xs whitespace-nowrap text-white shadow-pop rtl:translate-x-1/2">
                    {monthLabel(m.month)} · {formatILS(m.revenue, { decimals: false })} · {m.orders} הזמנות
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-1 flex gap-0.5 text-[10px] text-muted">
            {monthly.map((m) => <div key={m.month} className="flex-1 truncate text-center">{monthLabel(m.month)}</div>)}
          </div>
          <details className="mt-3 text-sm">
            <summary className="cursor-pointer text-xs text-muted">טבלת נתונים</summary>
            <table className="mt-2 w-full text-xs tabular">
              <tbody className="divide-y divide-line/70">
                {monthly.map((m) => (
                  <tr key={m.month}><td className="py-1">{monthLabel(m.month)}</td><td className="text-end">{m.orders} הזמנות</td><td className="text-end">{m.customers} לקוחות</td><td className="text-end font-semibold">{formatILS(m.revenue, { decimals: false })}</td></tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}
    </Card>
  );
}
